<template>

<div id="listWidget" class="mb-5">
    <ul class="breadcrumb">
      <li v-if="zone" class="breadcrumb-item">{{ zone?.name }}</li>
      <li class="breadcrumb-item active">가상스위치 제어기 등록</li>
    </ul>
    <div>
      <!-- <h4 class="mb-2">가상스위치 제어기 등록</h4> -->

      <!-- 단계별 안내 -->
      <div class="instruction-card card-item mb-2">
        <div class="instruction-header">
          <i class="fa-solid fa-list-ol me-2 text-primary"></i>
          <span class="instruction-title">등록 단계</span>
        </div>
        <div class="instruction-body">
          <div class="instruction-step">
            <div class="instruction-step-number">1</div>
            <div class="instruction-step-content">
              <span
                >가상스위치 제어기의 <strong class="text-primary">리셋(RESET) 버튼</strong>을 눌러서
                세팅 모드로 진입하시고 LED를 확인하십시오.</span
              >
            </div>
          </div>
          <div class="instruction-step">
            <div class="instruction-step-number">2</div>
            <div class="instruction-step-content">
              <span
                ><strong class="text-primary">연결 버튼</strong>을 눌러서
                가상스위치 제어기에 접속하십시오.</span
              >
            </div>
          </div>
          <div class="instruction-step">
            <div class="instruction-step-number">3</div>
            <div class="instruction-step-content">
              <span>로컬 WiFi 정보를 설정해주십시오.</span>
            </div>
          </div>
          <div class="instruction-step">
            <div class="instruction-step-number">4</div>
            <div class="instruction-step-content">
              <span>가상스위치 제어기를 서버에 등록합니다.</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 경고 메시지 -->
      <div class="warning-box mb-3">
        <i class="fa-solid fa-triangle-exclamation me-2"></i>
        <span>가상스위치 제어기 설정시 한 장치만 활성화 되어 있어야합니다.</span>
      </div>

      <!-- 연결 버튼 -->
      <div class="action-button-container mb-3">
        <button
          type="button"
          class="btn btn-primary w-100"
          @click="connectionDialog.show"
          :disabled="isRegistrationComplete"
        >
          <i class="fa-solid fa-link me-2"></i>
          {{ isRegistrationComplete ? '완료' : '연결' }}
        </button>
        <!-- 디버깅용 임시 표시 -->
        <!-- <div class="text-muted small mt-1" style="font-size: 0.7rem">
          Debug: _id="{{ connectedController._id }}", step2="{{ registrationSteps[2]?.status }}", disabled={{ isRegistrationComplete }}
        </div> -->
      </div>
      <!-- <div class="text-center text-muted" style="font-size: 0.9rem">
        연결 버튼을 누르면 연결 방식을 선택할 수 있습니다.
      </div> -->

    </div>

    <!-- 연결된 컨트롤러 정보 카드 -->
    <ControllerCard
      v-if="connectedController._id"
      :controller="connectedController"
      controller-type="가상스위치 제어기"
      mode="registration"
      @close="connectedController._id = ''"
    />

    <ControllerConnectionDialog
      :visible="connectionDialog.visible"
      title="가상스위치 제어기 연결 및 설정"
      actionType="register"
      defaultConnectionType="ble"
      :needsProvisioning="true"
      :needsWifiInput="true"
      :initialWifiSsid="settingParams.ssid"
      :initialWifiPassword="settingParams.pwd"
      @complete="onConnectionComplete"
      @cancel="connectionDialog.hide"
    />
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import Axios, { ZoneApi } from '@/api';
import useModal from '@/common/hooks/useModal';
import ControllerConnectionDialog from './components/ControllerConnectionDialog.vue';
import { SettingConfig } from '@/common/constants';

const { emitter } =
  getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const router = useRouter();
const route = useRoute();

const connectionDialog = reactive(useModal());
const settingParams = ref({ ssid: '', pwd: '' });
const zone = ref(undefined);
const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});

const registrationSteps = ref([
  { title: '연결 및 설정', status: 'pending', message: '', error: '' },
  { title: '서버 등록', status: 'pending', message: '', error: '' },
  { title: '완료', status: 'pending', message: '', error: '' },
]);

const updateStepStatus = (stepIndex, status, message = '', error = '') => {
  if (stepIndex >= 0 && stepIndex < registrationSteps.value.length) {
    registrationSteps.value[stepIndex].status = status;
    registrationSteps.value[stepIndex].message = message;
    registrationSteps.value[stepIndex].error = error;
  }
};

const resetSteps = () => {
  registrationSteps.value.forEach((step) => {
    step.status = 'pending';
    step.message = '';
    step.error = '';
  });
};

const showPopAlert = (msg) => {
  emitter.emit('showPopAlert', {
    description: msg,
  });
};

const onRegistClick = () => {
  if (!route.params.zoneId) {
    emitter.emit('showPopAlert', {
      description: '영역이 없거나 선택되지 않았습니다. 영역을 선택한 뒤 다시 시도해주세요.',
    });
    return;
  }
  connectionDialog.show();
};

const onConnectionComplete = async (result) => {
  try {
    if (!route.params.zoneId) {
      showPopAlert('영역이 없거나 선택되지 않았습니다. 영역을 선택한 뒤 다시 시도해주세요.');
      return;
    }

    connectedController.value = result.controller;

    if (result.wifiSsid && result.wifiPassword) {
      settingParams.value.ssid = result.wifiSsid;
      settingParams.value.pwd = result.wifiPassword;
    }

    updateStepStatus(0, 'completed', '연결 및 설정 완료');

    updateStepStatus(1, 'in_progress', '서버 등록 중...');
    emitter.emit('showLoading');

    await onRegist();

    updateStepStatus(1, 'completed', '서버 등록 완료');
    updateStepStatus(2, 'completed', '완료');
    emitter.emit('hideLoading');

    await store.dispatch('home/fetchAllZones');

    emitter.emit('showPopConfirm', {
      description:
        '가상스위치 제어기가 등록되었습니다.\n가상스위치 포트 매핑 화면으로 이동하시겠습니까?',
      okCallback: () => {
        router.push(`/setting/virtual/update/${route.params.zoneId}`);
      },
      cancelCallback: () => {
        router.back();
      },
    });
  } catch (e) {
    emitter.emit('hideLoading');

    const err =
      typeof e === 'string'
        ? { message: e }
        : e instanceof Error
          ? { message: e.message }
          : e || {};
    if (err.resultCode === '1001' || err.message?.includes('이미 등록된')) {
      const errorMessage =
        err.resultMessage ||
        err.message ||
        '이미 등록된 컨트롤러입니다.';
      updateStepStatus(1, 'failed', '', errorMessage);
      showPopAlert(errorMessage);
    } else {
      const errorMessage =
        err.resultMessage || err.message || '서버 등록 실패';
      updateStepStatus(1, 'failed', '', errorMessage);
      showPopAlert(errorMessage);
    }
  }
};

// MQTT 서버가 컨트롤러의 version 메시지를 받으면 자동으로 tempController를 생성하므로
// 앱에서는 tempController 생성 단계가 필요 없습니다.
const onRegist = async () => {
  const zid = route.params.zoneId;
  if (!zid) {
    return Promise.reject(new Error('영역 ID가 없습니다.'));
  }

  const request = () => {
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await Axios.post(
            ZoneApi.REGIST_ZONE_CTRLS.replace(':id', zid),
            connectedController.value,
          );

          if (result?.resultCode === '0000') {
            await requestZone();
          }
          resolve(result);
        } catch (e) {
          resolve(e);
        }
      }, SettingConfig.requestInterval);
    });
  };

  return new Promise(async (resolve, reject) => {
    try {
      let result = undefined;
      for (let i = 0; i < SettingConfig.requestCount; i++) {
        emitter.emit(
          'setLoadingText',
          i === 0
            ? '가상스위치 제어기 정보를 서버에 등록중입니다.'
            : `가상스위치 제어기 정보를 서버에 ${i + 1}번째 등록 시도하고 있습니다.`,
        );
        result = await request();
        if (result?.resultCode === '0000') {
          resolve();
          return;
        }
        if (result?.resultCode === '1001') {
          const errorMessage =
            result?.resultMessage ||
            '이미 등록된 컨트롤러입니다.';
          updateStepStatus(2, 'failed', '', errorMessage);
          reject({
            ...result,
            message: errorMessage,
          });
          emitter.emit('hideLoading', true);
          return;
        }
      }

      updateStepStatus(2, 'failed', '', result?.resultMessage || '서버 등록 실패');
      reject(
        result?.resultMessage?.length > 0
          ? result.resultMessage
          : '가상스위치 제어기 등록에 실패했습니다.',
      );
      emitter.emit('hideLoading', true);
    } catch (e) {
      console.log(e);
      reject('가상스위치 제어기 추가 중 오류가 발생하였습니다.');
      emitter.emit('hideLoading', true);
    }
  });
};

const requestZone = async () => {
  const zid = route.params.zoneId;
  if (!zid) {
    return Promise.reject('영역 ID가 없습니다.');
  }
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.get(
        ZoneApi.ZONE_INFO.replace(':id', zid),
      );
      if (result.resultCode === '0000') {
        zone.value = result.resultData;
        resolve(result);
      } else {
        reject(result.resultMessage);
      }
    } catch (error) {
      reject('영역 조회에 실패했습니다.');
    }
  });
};

onBeforeMount(() => {
  connectedController.value._id = '';
  connectedController.value.version = '';
  connectedController.value.model = '';
  resetSteps();
  emitter.emit('onChangeHeaderSetting',{title:'가상스위치 제어기 등록',isShowMenu:false,isShowZone:false,isShowProfile:false,isShowRightHome:true});
});
onBeforeUnmount(() =>{
  emitter.emit('hideLoading', true);
  emitter.emit('onChangeHeaderSetting',{title:'',isShowMenu:true,isShowZone:true,isShowProfile:true,isShowRightHome:false});
});

onMounted(() => {
  if (route.params.zoneId) {
    requestZone().catch(() => {});
  }
});
</script>

<style scoped>
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.instruction-step:last-child {
  margin-bottom: 0;
}

.instruction-step-number {
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0d6efd;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
}

.instruction-step-content {
  flex: 1;
  padding-top: 0.1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #495057;
}

.warning-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.5rem;
  color: #856404;
  font-size: 0.8rem;
}

.warning-box i {
  color: #ffc107;
  flex-shrink: 0;
  margin-top: 0.1rem;
  font-size: 0.85rem;
}

.action-button-container {
  margin-top: 0;
}

.action-button-container .btn {
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.action-button-container .btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.step-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-item {
  position: relative;
  display: flex;
  flex-direction: column;
}

.step-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;
}

.step-indicator {
  width: 50px;
  height: 50px;
  min-width: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-pending {
  background-color: #e9ecef;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.step-in-progress {
  background-color: #0d6efd;
  color: white;
  border: 2px solid #0d6efd;
  animation: pulse 1.5s ease-in-out infinite;
}

.step-completed {
  background-color: #198754;
  color: white;
  border: 2px solid #198754;
}

.step-failed {
  background-color: #dc3545;
  color: white;
  border: 2px solid #dc3545;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.step-label {
  flex: 1;
  padding-top: 0.5rem;
}

.step-connector {
  position: absolute;
  left: 25px;
  top: 75px;
  width: 2px;
  height: calc(100% - 50px);
  background-color: #dee2e6;
  z-index: 0;
}

.connector-active {
  background-color: #198754;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.model-guide-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.model-section {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}

.model-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.model-list {
  padding-left: 2rem;
}

.model-item {
  margin-bottom: 0.5rem;
}

.model-code {
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
}

.model-codes {
  color: #0d6efd;
  font-weight: 600;
  font-size: 0.85rem;
  margin-left: 0.5rem;
}

.model-description {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.5;
  margin-top: 0.25rem;
}
</style>
